
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Download, X, FileImage, FileText, User, GraduationCap, Award, Percent } from "lucide-react";

interface StudentProfileFormatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (format: 'jpg' | 'pdf', instructorName?: string, directorName?: string, grade?: string, percentage?: string) => void;
  profileType: 'profile' | 'programs' | 'enrollment' | 'certificate';
  studentName: string;
}

const StudentProfileFormatDialog = ({ 
  isOpen, 
  onClose, 
  onDownload,
  profileType,
  studentName
}: StudentProfileFormatDialogProps) => {
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');
  const [instructorName, setInstructorName] = useState('Dr. Ahmad Ali');
  const [directorName, setDirectorName] = useState('Prof. Mohammad Hassan');
  const [selectedGrade, setSelectedGrade] = useState('Excellent');
  const [percentage, setPercentage] = useState('');

  const handleDownload = () => {
    if (profileType === 'certificate') {
      onDownload(selectedFormat, instructorName, directorName, selectedGrade, percentage);
    } else {
      onDownload(selectedFormat);
    }
    onClose();
  };

  const getTitle = () => {
    switch (profileType) {
      case 'profile': return 'Student Profile';
      case 'programs': return 'Programs & Fees';
      case 'enrollment': return 'Enrollment Details';
      case 'certificate': return 'Certificate';
      default: return 'Student Document';
    }
  };

  const getDescription = () => {
    switch (profileType) {
      case 'profile': return 'Download student profile with personal information';
      case 'programs': return 'Download student programs and fees information';
      case 'enrollment': return 'Download student enrollment details';
      case 'certificate': return 'Download student completion certificate';
      default: return 'Download student document';
    }
  };

  const isCertificate = profileType === 'certificate';

  const gradeOptions = [
    { value: 'Excellent', label: 'Excellent' },
    { value: 'Good', label: 'Good' },
    { value: 'Poor', label: 'Poor' },
    { value: 'Failed', label: 'Failed' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-gray-800 max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between text-white">
            Download {getTitle()}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0 text-white hover:bg-gray-800"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {getDescription()} for {studentName}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {isCertificate && (
              <div className="space-y-4 p-4 border border-gray-700 rounded-lg bg-gray-800">
                <h3 className="text-sm font-medium text-white">Certificate Details</h3>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="instructor" className="text-sm font-medium flex items-center gap-2 text-white">
                      <GraduationCap className="h-4 w-4" />
                      Instructor Name
                    </Label>
                    <Input
                      id="instructor"
                      value={instructorName}
                      onChange={(e) => setInstructorName(e.target.value)}
                      placeholder="Enter instructor name"
                      className="mt-1 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="director" className="text-sm font-medium flex items-center gap-2 text-white">
                      <User className="h-4 w-4" />
                      Director Name
                    </Label>
                    <Input
                      id="director"
                      value={directorName}
                      onChange={(e) => setDirectorName(e.target.value)}
                      placeholder="Enter director name"
                      className="mt-1 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                  </div>

                  <div>
                    <Label htmlFor="grade" className="text-sm font-medium flex items-center gap-2 text-white">
                      <Award className="h-4 w-4" />
                      Grade
                    </Label>
                    <Select value={selectedGrade} onValueChange={setSelectedGrade}>
                      <SelectTrigger className="mt-1 bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-700 border-gray-600">
                        {gradeOptions.map((grade) => (
                          <SelectItem key={grade.value} value={grade.value} className="text-white hover:bg-gray-600">
                            {grade.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="percentage" className="text-sm font-medium flex items-center gap-2 text-white">
                      <Percent className="h-4 w-4" />
                      Percentage (Optional)
                    </Label>
                    <Input
                      id="percentage"
                      type="number"
                      min="0"
                      max="100"
                      value={percentage}
                      onChange={(e) => setPercentage(e.target.value)}
                      placeholder="Enter percentage (0-100)"
                      className="mt-1 bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              </div>
            )}

            <RadioGroup value={selectedFormat} onValueChange={(value) => setSelectedFormat(value as 'jpg' | 'pdf')}>
              <div className="flex items-center space-x-3 p-3 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors">
                <RadioGroupItem value="jpg" id="jpg" className="border-gray-600 text-white" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileImage className="h-5 w-5 text-blue-400" />
                  <div>
                    <Label htmlFor="jpg" className="text-base font-medium cursor-pointer text-white">
                      JPG Image
                    </Label>
                    <p className="text-sm text-gray-400">
                      High-quality image format, ultra-fast download
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors">
                <RadioGroupItem value="pdf" id="pdf" className="border-gray-600 text-white" />
                <div className="flex items-center space-x-3 flex-1">
                  <FileText className="h-5 w-5 text-red-400" />
                  <div>
                    <Label htmlFor="pdf" className="text-base font-medium cursor-pointer text-white">
                      PDF Document
                    </Label>
                    <p className="text-sm text-gray-400">
                      Professional document format, perfect for printing
                    </p>
                  </div>
                </div>
              </div>
            </RadioGroup>
          </div>
        </ScrollArea>

        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-700">
          <Button variant="outline" onClick={onClose} className="border-gray-600 text-white hover:bg-gray-800">
            Cancel
          </Button>
          <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Download className="w-4 h-4 mr-2" />
            Download {selectedFormat.toUpperCase()}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentProfileFormatDialog;
